import React, { FC, useCallback, useState } from 'react'
import { ControllerProps } from 'react-hook-form'
import get from 'lodash/get'
import ControlledHiddenInput from 'transfix-form/components/ControlledHiddenInput'
import { Facility, IFacilityProps } from 'apps/fleet-planner/components/load-details/stops/Facility'
import { useCreateLoadFormContext } from 'apps/fleet-planner/context'
import { IStop } from 'apps/fleet-planner/types'

type IBaseControllerProps = ControllerProps<React.ComponentType<IFacilityProps>>

export interface IControlledFacilityProps
  extends Pick<IBaseControllerProps, 'control' | 'defaultValue'> {
  /**
   * Optionally prepends a string to each <Controller />'s name.
   * Useful inside of useFieldArray() loop
   * @default undefined
   */
  namePrefix?: string
  setValue: (name: string, value: unknown) => void
  defaultValue: IFacilityProps['stop']
  /**
   * Optional props for <Facility /> component
   */
  FacilityProps?: Partial<IFacilityProps>
}

/**
 * Helper component for use with React Hook Form
 * @see https://react-hook-form.com/api#Controller
 * @example
 * ```
 * <ControlledFacility
 *   name={`stop[${stopIndex}]`}
 *   control={control}
 *   setValue={setValue}
 *   FacilityProps={{
 *     dataTest: `${stopType}-city-${stopSequence}`
 *   }} />
 * ```
 */
export const ControlledFacility: FC<IControlledFacilityProps> = ({
  control,
  setValue,
  namePrefix = '',
  defaultValue = {},
  FacilityProps,
}) => {
  const { errors: formErrors, trigger } = useCreateLoadFormContext()
  const [stop, setStop] = useState<Partial<IStop>>(defaultValue as Partial<IStop>)

  /**
   * Returns combination of namePrefix and keyName
   */
  const getInputNameWithPrefix = useCallback(
    (keyName: string): string => {
      const fullName = namePrefix ? `${namePrefix}.` : '' // adds '.' to path
      return `${fullName}${keyName}`
    },
    [namePrefix]
  )

  /**
   * May also need: isNewFacility?: boolean | undefined
   */
  const facilityChangeHandler = useCallback(
    (changes: Partial<IStop> = {}) => {
      setValue(getInputNameWithPrefix('address'), changes.address)
      setValue(getInputNameWithPrefix('facility'), changes.facility)
      setValue(getInputNameWithPrefix('facilityContact'), changes.facilityContact)
      setValue(getInputNameWithPrefix('stateCode'), changes.stateCode)
      setValue(getInputNameWithPrefix('city'), changes.city)

      // Update local state too
      setStop(changes)

      // If there's an existing error, re-trigger the validation
      const inputName = getInputNameWithPrefix('facility.name')
      if (get(formErrors, `${inputName}.message`, '')) {
        trigger(inputName)
      }
    },
    [formErrors, getInputNameWithPrefix, setValue, trigger]
  )

  /**
   * Triggers validation on blur
   */
  const blurHandler = useCallback(() => {
    trigger(getInputNameWithPrefix('facility.name'))
  }, [getInputNameWithPrefix, trigger])

  const { dataTest, errors = {}, large = false, required = false, icon = undefined } =
    FacilityProps || {}

  return (
    <>
      <Facility
        stop={stop}
        onStopChange={facilityChangeHandler}
        errors={errors}
        dataTest={dataTest}
        large={large}
        required={required}
        icon={icon}
        onBlur={blurHandler}
      />
      <ControlledHiddenInput
        name={getInputNameWithPrefix('facility')}
        control={control}
        defaultValue={{ ...defaultValue?.facility }}
      />
      <ControlledHiddenInput
        name={getInputNameWithPrefix('address')}
        control={control}
        defaultValue={{ ...defaultValue?.address }}
      />
      <ControlledHiddenInput
        name={getInputNameWithPrefix('facilityContact')}
        control={control}
        defaultValue={{ ...defaultValue?.facilityContact }}
      />
      <ControlledHiddenInput
        name={getInputNameWithPrefix('stateCode')}
        control={control}
        defaultValue={defaultValue?.stateCode}
      />
      <ControlledHiddenInput
        name={getInputNameWithPrefix('city')}
        control={control}
        defaultValue={defaultValue?.city}
      />
    </>
  )
}
